import { useMemo, useState } from "react";
import { resourceSites, resourceCategories } from "@/mocks/resources";
import PageHeader from "@/components/feature/PageHeader";
import SectionHeading from "@/components/feature/SectionHeading";

function ResourceCard({
  site,
}: {
  site: (typeof resourceSites)[number];
}) {
  return (
    <article className="group rounded-xl bg-background-100 border border-background-200/70 p-5 hover:border-primary-400/60 transition-all hover:-translate-y-1">
      <div className="flex items-center justify-between">
        <span
          className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-bold text-background-50"
          style={{ backgroundColor: site.color }}
        >
          <i className="ri-verified-badge-fill"></i>
          {site.category}
        </span>
        <span className="text-accent-500 text-xs font-bold">{site.stability}</span>
      </div>
      <div className="mt-4 flex items-center gap-3">
        <span className="w-11 h-11 rounded-lg flex items-center justify-center text-lg font-heading text-background-50"
          style={{ backgroundColor: site.color }}>
          {site.name.slice(0, 1)}
        </span>
        <div>
          <h3 className="font-heading text-lg text-foreground-950">{site.name}</h3>
          <p className="text-xs text-foreground-600">{site.tagline}</p>
        </div>
      </div>
      <ul className="mt-4 space-y-1.5">
        {site.features.map((f) => (
          <li key={f} className="flex items-center gap-2 text-xs text-foreground-700">
            <i className="ri-checkbox-circle-fill text-secondary-500"></i>
            {f}
          </li>
        ))}
      </ul>
      <div className="mt-4 pt-4 border-t border-background-200/70 flex items-center justify-between text-[11px] text-foreground-600">
        <span>{site.update}</span>
        <span>收录 {site.works} 作品</span>
      </div>
    </article>
  );
}

export default function ZiyuanPage() {
  const [keyword, setKeyword] = useState("");
  const [cat, setCat] = useState("all");

  const list = useMemo(() => {
    return resourceSites.filter((s) => {
      const okCat = cat === "all" || s.category === cat;
      const kw = keyword.trim();
      const okKw = !kw || s.name.includes(kw) || s.tagline.includes(kw) || s.features.join("").includes(kw);
      return okCat && okKw;
    });
  }, [keyword, cat]);

  return (
    <>
      <PageHeader
        title="漫画资源导航"
        subtitle="한국 무료 만화 사이트 · 网站大全"
        desc="从日更无遮拦到高清原版，从老牌稳定站到APP网页双端，这里汇总了目前靠谱的韩国免费漫画资源入口，按需求分类，收藏即用。"
        crumbs={[{ label: "资源导航" }]}
      />

      <section className="mx-auto max-w-7xl px-4 md:px-6 py-8 md:py-12">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap gap-2">
            {resourceCategories.map((c) => (
              <button
                key={c.value}
                type="button"
                onClick={() => setCat(c.value)}
                className={`px-3.5 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors ${
                  cat === c.value
                    ? "bg-primary-500 text-background-50 font-bold"
                    : "bg-background-100 text-foreground-700 hover:text-primary-500"
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>
          <div className="relative md:w-64">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-600 text-sm"></i>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索资源站…"
              className="w-full pl-9 pr-3 py-2 rounded-md bg-background-100 border border-background-300/60 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-primary-400"
            />
          </div>
        </div>

        <p className="mt-5 text-sm text-foreground-600">
          共收录 <span className="text-accent-500 font-bold">{list.length}</span> 个资源入口
        </p>

        <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((s) => (
            <ResourceCard key={s.id} site={s} />
          ))}
        </div>

        {list.length === 0 && (
          <p className="mt-10 text-center text-foreground-600 text-sm">
            没有匹配的资源站，试试切换分类或换一个关键词。
          </p>
        )}
      </section>

      <section className="mx-auto max-w-7xl px-4 md:px-6 py-8 md:py-12">
        <SectionHeading
          eyebrow="HOW TO CHOOSE"
          title="如何选择合适的资源入口"
          desc="不同需求的读者，适合不同的资源类型。这里给你三个参考维度。"
        />
        <div className="grid md:grid-cols-3 gap-4">
          <div className="rounded-xl bg-background-100 border border-background-200/70 p-5">
            <span className="w-11 h-11 rounded-full bg-primary-100/60 text-primary-500 flex items-center justify-center text-xl"><i className="ri-flashlight-fill"></i></span>
            <h3 className="mt-3 font-heading text-base text-foreground-950">追更党选"日更"</h3>
            <p className="mt-2 text-xs text-foreground-600 leading-relaxed">每天都有新内容，更新节奏稳定，适合高频追更、想每天看到新作的读者。</p>
          </div>
          <div className="rounded-xl bg-background-100 border border-background-200/70 p-5">
            <span className="w-11 h-11 rounded-full bg-secondary-100/70 text-secondary-500 flex items-center justify-center text-xl"><i className="ri-smartphone-line"></i></span>
            <h3 className="mt-3 font-heading text-base text-foreground-950">手机党选"APP/网页"</h3>
            <p className="mt-2 text-xs text-foreground-600 leading-relaxed">支持缓存离线与追更提醒，通勤、睡前看漫更顺畅，体验更完整。</p>
          </div>
          <div className="rounded-xl bg-background-100 border border-background-200/70 p-5">
            <span className="w-11 h-11 rounded-full bg-accent-500/15 text-accent-500 flex items-center justify-center text-xl"><i className="ri-shield-check-line"></i></span>
            <h3 className="mt-3 font-heading text-base text-foreground-950">省心派选"老牌稳定"</h3>
            <p className="mt-2 text-xs text-foreground-600 leading-relaxed">运营时间长、存档完整、体验稳定，不想折腾的首选，追更更省心。</p>
          </div>
        </div>
      </section>
    </>
  );
}